import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Send, ExternalLink, ArrowLeft } from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL || "";

export default function CanvaEditorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const designId = searchParams.get("designId") || "";
  const editUrl = searchParams.get("editUrl") || "";
  const returnTo = searchParams.get("returnTo") || "/mockup-studio";

  const [isExporting, setIsExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canvaOpened, setCanvaOpened] = useState(false);

  useEffect(() => {
    if (editUrl && !canvaOpened) {
      window.open(editUrl, "_blank");
      setCanvaOpened(true);
    }
  }, [editUrl, canvaOpened]);

  async function handleSendToERC() {
    if (!designId) return;
    setIsExporting(true);
    setError(null);
    try {
      const token = localStorage.getItem("erc_token") || "";

      // Step 1: Export from Canva
      const exportRes = await fetch(`${API_BASE}/api/canva/export-design`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ designId }),
      });
      const exportData = await exportRes.json();

      if (!exportRes.ok) {
        throw new Error(exportData.error || "Export hatası");
      }

      const urls = exportData.data?.urls;
      const pngUrl = Array.isArray(urls) ? urls[0] : urls;
      if (!pngUrl) {
        throw new Error("Export URL alınamadı");
      }

      // Step 2: Save to ERC via localStorage event
      // The MockupStudioPage listens for this
      localStorage.setItem("canva_import_result", JSON.stringify({
        designUrl: pngUrl,
        designId,
        timestamp: Date.now(),
      }));

      // Trigger storage event for same-tab detection
      window.dispatchEvent(new StorageEvent("storage", {
        key: "canva_import_result",
        newValue: JSON.stringify({ designUrl: pngUrl, designId, timestamp: Date.now() }),
      }));

      setExportDone(true);
    } catch (err: any) {
      setError(err.message || "Bilinmeyen hata");
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex flex-col items-center justify-center p-8">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#7B2FF2] text-white mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12h8M12 8v8" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Canva Editörü</h1>
          <p className="text-sm text-gray-500">
            Canva'da tasarımınızı düzenleyin, bitirdiğinizde aşağıdaki butona tıklayın
          </p>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 space-y-4">
          {!exportDone ? (
            <>
              <div className="flex items-center gap-3 text-sm text-purple-700 bg-purple-50 rounded-lg p-3">
                <ExternalLink className="h-5 w-5 flex-shrink-0" />
                <span>Canva editörü yeni sekmede açıldı. Düzenlemenizi tamamlayın ve geri dönün.</span>
              </div>

              {/* Reopen Canva */}
              {editUrl && (
                <button
                  onClick={() => window.open(editUrl, "_blank")}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-purple-200 px-4 py-3 text-sm font-medium text-purple-700 hover:bg-purple-50 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  Canva'yı Tekrar Aç
                </button>
              )}

              {/* Send to ERC */}
              <button
                onClick={handleSendToERC}
                disabled={isExporting || !designId}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#7B2FF2] px-4 py-4 text-base font-semibold text-white hover:bg-[#6B21D6] disabled:opacity-50 transition-colors shadow-md"
              >
                {isExporting ? (
                  <><Loader2 className="h-5 w-5 animate-spin" />Export ediliyor...</>
                ) : (
                  <><Send className="h-5 w-5" />ERC'ye Gönder</>
                )}
              </button>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
                  {error}
                </div>
              )}
            </>
          ) : (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-gray-900">Tasarım başarıyla alındı!</p>
              <p className="text-sm text-gray-500">Mockup Studio'ya dönüp tasarımı kullanabilirsiniz.</p>
              <button
                onClick={() => navigate(returnTo)}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Mockup Studio'ya Dön
              </button>
            </div>
          )}
        </div>

        {/* Back link */}
        {!exportDone && (
          <button
            onClick={() => navigate(returnTo)}
            className="flex items-center justify-center gap-1 text-sm text-gray-400 hover:text-gray-600 mx-auto"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Geri Dön
          </button>
        )}
      </div>
    </div>
  );
}
